import { useLoaderData } from 'react-router';
import { ArrowUpRight } from 'lucide-react';

import { RoccoLink } from '@rocco/components/link';
import { ProfessionalTileLogo } from '@rocco/ui/tile-professional/components/ProfessionalTileLogo';
import type { loader } from '~/routes/_app.project.$urlSegment._index';

/**
 * Compact professional trust row under the mobile CTA cluster — the dark-theme
 * counterpart of the product page's `MobileSupplierTrustRow`. The circular logo
 * badge, the name, its discipline and the ArchiPro-only proof point ("N projects
 * on ArchiPro") sit beside the primary Book-a-Meeting action: people enquire
 * when they trust the practice, so the strongest signal belongs next to the CTA
 * rather than five sections down in the About-the-Professional block.
 *
 * Presentational trust echo — the full professional block still lives lower on
 * the page. Uses the revamp's editorial language on the dark ground: the
 * `ProfessionalTileLogo` badge, light ink / muted type, a hairline top border.
 */
export const ProjectSupplierTrustRow = () => {
    const { project } = useLoaderData<typeof loader>();
    const professional = project.Professional;
    if (!professional?.Title) return null;

    const role = professional.Categories?.[0]?.PrettyTitle ?? '';
    const projectCount = professional.ProjectCount ?? 0;

    return (
        <RoccoLink
            to={professional.Link}
            className="group flex items-center gap-3 border-t border-white/15 py-4"
        >
            {professional.CompanyLogo ? (
                <ProfessionalTileLogo
                    image={{
                        src: professional.CompanyLogo,
                        alt: `${professional.Title} logo`,
                    }}
                    background={professional.LogoBackgroundColor ?? null}
                    className="size-12 shrink-0"
                    contrastBorder
                />
            ) : null}
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex items-center gap-1.5">
                    <span className="truncate font-sans text-base leading-[1.2] font-medium tracking-[-0.02em] text-white">
                        {professional.Title}
                    </span>
                    <ArrowUpRight className="size-4 shrink-0 text-white transition-transform duration-200 ease-out group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
                {role ? (
                    <span className="truncate font-sans text-[0.8125rem] leading-[1.2] text-white/55">
                        {role}
                    </span>
                ) : null}
                {projectCount > 0 ? (
                    <span className="font-sans text-[0.8125rem] leading-[1.2] text-white">
                        <span className="font-medium">
                            {projectCount}{' '}
                            {projectCount === 1 ? 'project' : 'projects'}
                        </span>{' '}
                        <span className="text-white/55">on ArchiPro</span>
                    </span>
                ) : null}
            </span>
        </RoccoLink>
    );
};
